/* eslint-disable */
import get from 'lodash/get';
import { AUTH_SERVICE_STORAGE_ENTITY_KEY } from '../../../Constants/Auth/Auth.constants';
import {
  USER_SERVICE_PRESENCE_EVENT_NAMES,
  USER_SERVICE_PRESENCE_STATUS_AWAY,
  USER_SERVICE_PRESENCE_STATUS_PRESENT,
  USER_SERVICE_PRESENCE_STATUS_UNKNOWN,
  USER_SERVICE_PRESENCE_TIMEOUT_THRESHOLD,
} from '../../../Constants/User/User.constants';
import throwAuthCouldNotCreateHeadersWhilstNotLoggedInError from '../../../Errors/Auth/AuthCouldNotCreateHeadersWhilstNotLoggedIn.error';
import throwAuthCouldNotInitStateError from '../../../Errors/Auth/AuthCouldNotInitState.error';
import throwAuthInvalidHandlerTypeError from '../../../Errors/Auth/AuthInvalidHandlerType.error';
import throwAuthInvalidTokenDurationError from '../../../Errors/Auth/AuthInvalidTokenDuration.error';
import throwMethodNotImplementedError from '../../../Errors/General/MethodNotImplemented.error';
import throwModelCouldNotUnsubscribeError from '../../../Errors/Model/ModelCouldNotUnsubscribe.error';
import isFunction from '../../../Support/Is/Function/IsFunction';
import envGet from '../../../Support/Env/Get/EnvGet.support';

const apikey = envGet({ key: `REACT_APP_API_KEY` });

const doc = document;

/**
 * AuthAdapter
 * @class AuthAdapter
 * @implements {AuthServiceAdapterContract}
 */
class AuthAdapter {
  /**
   * The current `auth` object
   * @type {AuthEntity | null}
   * @memberof AuthAdapter
   */
  _auth = null;

  /**
   * The list of handlers that will respond to auth presence changes
   * @type {Array<AuthServicePresenceChangedHandlerInvoker>}
   * @memberof AuthAdapter
   */
  _authPresenceChangedHandlers = [];

  /**
   * The list of handlers that will respond to auth state changes
   * @type {Array<AuthServiceStateChangedHandlerInvoker>}
   * @memberof AuthAdapter
   */
  _authStateChangedHandlers = [];

  /**
   * The current `presence`
   * @type {AuthServicePresenceStatus}
   * @memberof AuthAdapter
   */
  _presence = USER_SERVICE_PRESENCE_STATUS_UNKNOWN;

  /**
   * The `setTimeout` `id` of the queued expiry
   * @type {number | undefined}
   * @memberof AuthAdapter
   */
  _queuedExpiryTimeoutId;

  /**
   * How long the _token_'s should live
   * @type {number} The amount of time in milliseconds
   * @memberof AuthAdapter
   */
  _tokenDuration = 0;

  /**
   * The `StorageService`
   * @type {StorageService}
   * @memberof AuthAdapter
   */
  storageService;

  /**
   * Creates an instance of AuthAdapter
   * @param {AuthAdapterCreateDto} dto The params required to create an instance of `AuthAdapter`
   * @memberof AuthAdapter
   */
  constructor({ storageService }) {
    this.storageService = storageService;
    this._init();
  }

  /**
   * Change the `presence` of the auth
   * @param {AuthServicePresenceStatus} presence The auth `presence` to change
   * @returns {Promise<AuthServicePresenceStatus>} The new auth `presence`
   * @memberof AuthAdapter
   */
  _changePresence = async (presence) => {
    if (this._presence === presence) {
      return this._presence;
    }
    this._presence = presence;
    this._authPresenceChangedHandlers.forEach((authPresenceChangedHandler) => {
      authPresenceChangedHandler();
    });
    const auth = this.getAuth();
    const userPresence = get(auth, `user.presence`);
    if (auth === null || userPresence === this._presence) {
      return this._presence;
    }
    const newAuth = {
      ...auth,
      user: {
        ...auth.user,
        presence: this._presence,
      },
    };
    await this._changeState(newAuth);
    return this._presence;
  };

  /**
   * Change the `state` of the auth
   * @param {AuthEntity | null} newAuth The new auth `state` to change to
   * @returns {Promise<AuthEntity | null>} The new auth `state`
   * @memberof AuthAdapter
   */
  _changeState = async (newAuth) => {
    // console.log(`_changeState`, newAuth)
    await this.storageService.setItem(AUTH_SERVICE_STORAGE_ENTITY_KEY, newAuth);
    this._queueExpiry(newAuth);
    this._auth = newAuth;
    this._authStateChangedHandlers.forEach((authStateChangedHandler) => {
      authStateChangedHandler();
    });
    return this.getAuth();
  };

  /**
   * Create a handler to respond to auth presence changes
   * @param {AuthServicePresenceChangedHandler} authPresenceChangedHandler A method to be continously invoked upon auth presence changes
   * @returns {AuthServicePresenceChangedHandlerInvoker} A Function that will _invoke_ the `handler`
   * @memberof AuthAdapter
   */
  _createAuthPresenceChangedHandler = (authPresenceChangedHandler) => {
    return () => {
      authPresenceChangedHandler(null, this._presence);
    };
  };

  /**
   * Create a handler to respond to auth state changes
   * @param {AuthServiceStateChangedHandler} authStateChangedHandler A method to be continously invoked upon auth state changes
   * @returns {AuthServiceStateChangedHandlerInvoker} A Function that will _invoke_ the `handler`
   * @memberof AuthAdapter
   */
  _createAuthStateChangedHandler = (authStateChangedHandler) => {
    return () => {
      authStateChangedHandler(null, this.getAuth());
    };
  };

  /**
   * Create an unsubscribe handler that can be used to stop listening to the auth presence changes
   * @param {AuthServicePresenceChangedHandler} authPresenceChangedHandler The handler to unsubscribe
   * @returns {AuthServicePresenceChangedUnsubscribe} A Function that will perform the unsubscribe
   * @memberof AuthAdapter
   */
  _createAuthPresenceChangedUnsubscribe = (authPresenceChangedHandler) => {
    return () => {
      const index = this._authPresenceChangedHandlers.findIndex((handler) => handler === authPresenceChangedHandler);
      if (index === -1) {
        return throwModelCouldNotUnsubscribeError(`authPresenceChangedHandler`);
      }
      this._authPresenceChangedHandlers.splice(index, 1);
    };
  };

  /**
   * Create an unsubscribe handler that can be used to stop listening to the auth state changes
   * @param {AuthServiceStateChangedHandler} authStateChangedHandler The handler to unsubscribe
   * @returns {AuthServiceStateChangedUnsubscribe} A Function that will perform the unsubscribe
   * @memberof AuthAdapter
   */
  _createAuthStateChangedUnsubscribe = (authStateChangedHandler) => {
    return () => {
      const index = this._authStateChangedHandlers.findIndex((handler) => handler === authStateChangedHandler);
      if (index === -1) {
        return throwModelCouldNotUnsubscribeError(`authStateChangedHandler`);
      }
      this._authStateChangedHandlers.splice(index, 1);
    };
  };

  /**
   * Determine when a given `auth` state will expire, falling back to `Date.now()` if the `auth` state has already expired
   * @param {AuthEntity | null} auth The `auth` state to check
   * @returns {number} The timestamp of when the expiry will occur
   * @memberof AuthAdapter
   */
  _getExpiresAt = (auth) => {
    const expiresAt = get(auth, `accessToken.expiresAt`, 0);
    const now = Date.now();
    if (expiresAt <= now) {
      return now;
    }
    return expiresAt;
  };

  /**
   * Determine how long the _token_'s shoud live
   * @returns {number} The amount of time in milliseconds
   * @memberof AuthAdapter
   */
  _getTokenDuration = () => {
    if (this._tokenDuration === undefined) {
      return throwAuthInvalidTokenDurationError(this._tokenDuration);
    }
    if (this._tokenDuration <= 0) {
      return throwAuthInvalidTokenDurationError(this._tokenDuration);
    }
    return this._tokenDuration;
  };

  /**
   * Initialise the various parts of the `AuthAdapter`
   * @returns {Promise<AuthEntity | null>} The new `auth` state
   * @memberof AuthAdapter
   */
  _init = async () => {
    try {
      /** @type {AuthEntity | null} */
      const auth = await this.storageService.getItem(AUTH_SERVICE_STORAGE_ENTITY_KEY);
      return this._initAuthState(auth);
    } catch (error) {
      return throwAuthCouldNotInitStateError(error);
    }
  };

  /**
   * Initialise the `presence` on boot to online and then queue up `_changePresence` for any future presence changes
   * @returns {void} void
   * @memberof AuthAdapter
   */
  _initAuthPresence = async () => {
    await this._changePresence(USER_SERVICE_PRESENCE_STATUS_PRESENT);
    let presenceTimeoutId;
    /**
     * Handle `presence` changes
     * @returns {void} void
     */
    const handler = async () => {
      if (presenceTimeoutId !== undefined) {
        clearTimeout(presenceTimeoutId);
      }
      await this._changePresence(USER_SERVICE_PRESENCE_STATUS_PRESENT);
      presenceTimeoutId = setTimeout(() => {
        this._changePresence(USER_SERVICE_PRESENCE_STATUS_AWAY);
      }, USER_SERVICE_PRESENCE_TIMEOUT_THRESHOLD);
    };
    USER_SERVICE_PRESENCE_EVENT_NAMES.forEach((eventName) => {
      doc.addEventListener(eventName, handler);
    });
  };

  /**
   * Determine if there is a session already persisted and emit that state if it is valid
   * @param {AuthEntity | null} auth The new `auth` state
   * @returns {Promise<AuthEntity | null>} The current `auth` state
   * @memberof AuthAdapter
   */
  _initAuthState = async (auth) => {
    const expiresAt = this._getExpiresAt(auth);
    const newAuth = expiresAt <= Date.now() ? null : auth;
    await this._changeState(newAuth);
    await this._initAuthPresence();
    return newAuth;
  };

  /**
   * Queue a state change when the provided `auth` session is due to expire
   * @param {AuthEntity} auth The state to base to queue off
   * @returns {void} void
   * @memberof AuthAdapter
   */
  _queueExpiry = (auth) => {
    const expiresAt = this._getExpiresAt(auth);
    const duration = expiresAt - Date.now();
    if (this._queuedExpiryTimeoutId !== undefined) {
      clearTimeout(this._queuedExpiryTimeoutId);
    }
    if (auth === null) {
      this.clearAuth();
      return;
    }
    this._queuedExpiryTimeoutId = setTimeout(() => {
      const newAuth = null;
      this.clearAuth();
      this._changeState(newAuth);
    }, duration);
  };

  /**
   * Get the current `auth`
   * @returns {AuthEntity | null} The current `auth`
   * @memberof AuthAdapter
   */
  getAuth = () => {
    return this._auth;
  };

  /**
   * Clears auth from storage. Does not log out the user.
   * @returns {void} void
   * @memberof AuthOktaAdapter
   */
  clearAuth = async () => {
    return throwMethodNotImplementedError(`clearAuth`);
  };

  /**
   * Ensure that the user is logged in and that they have a valid token that has not expired
   * @returns {Headers} The Headers with the included `Authorization` header.
   * @memberof AuthAdapter
   */
  createHeaders = () => {
    if (!this.isAuthenticated()) {
      return
      // return throwAuthCouldNotCreateHeadersWhilstNotLoggedInError();
    }
    /** @type {AuthEntity} */
    const { accessToken } = this.getAuth();
    const headers = {
      Authorization: `${accessToken.type} ${accessToken.token}`,
      apikey,
    };
    return headers;
  };

  /**
   * Determine if the user is currently authenticated
   * @returns {boolean} Whether or not the current user is authenticated or not
   * @memberof AuthAdapter
   */
  isAuthenticated = () => {
    const auth = this.getAuth();
    if (auth === null) {
      return false;
    }
    if (auth.expiresAt < Date.now()) {
      return false;
    }
    return true;
  };

  /**
   * Handle login success.
   * @param {any} response The auth success response
   * @param {any} tokenManager The token manager
   * @returns {Promise<AuthEntity>} The auth entity
   * @memberof AuthAdapter
   */
  loginSuccess = async (response, tokenManager) => {
    return throwMethodNotImplementedError(`loginSuccess`);
  };

  /**
   * Attempt to login a user with an `username` and a `password`
   * @param {AuthServiceLoginWithUsernameAndPasswordDto} dto The required params to login
   * @returns {Promise<AuthEntity>} The auth entity
   * @memberof AuthAdapter
   */
  loginWithUsernameAndPassword = async ({ password, username }) => {
    return throwMethodNotImplementedError(`loginWithUsernameAndPassword`);
  };

  /**
   * Logout a user
   * @returns {Promise<null>} The updated representation of the current user
   * @memberof AuthAdapter
   */
  logout = async () => {
    return throwMethodNotImplementedError(`logout`);
  };

  /**
   * Fire a callback when the auth presence changes
   * @param {AuthServicePresenceChangedHandler} handler The `handler` to listen to auth presence changes
   * @returns {AuthServicePresenceChangedUnsubscribe} The function used to unsubscribe from auth presence changes
   * @memberof AuthAdapter
   */
  onAuthPresenceChanged = (handler) => {
    if (!isFunction(handler)) {
      return throwAuthInvalidHandlerTypeError(handler);
    }
    const authPresenceChangedHandler = this._createAuthPresenceChangedHandler(handler);
    this._authPresenceChangedHandlers.push(authPresenceChangedHandler);
    const unsubscribe = this._createAuthPresenceChangedUnsubscribe(authPresenceChangedHandler);
    return unsubscribe;
  };

  /**
   * Fire a callback when the auth state changes
   * @param {AuthServiceStateChangedHandler} handler The `handler` to listen to auth state changes
   * @returns {AuthServiceStateChangedUnsubscribe} The function used to unsubscribe from auth state changes
   * @memberof AuthAdapter
   */
  onAuthStateChanged = (handler) => {
    if (!isFunction(handler)) {
      return throwAuthInvalidHandlerTypeError(handler);
    }
    const authStateChangedHandler = this._createAuthStateChangedHandler(handler);
    this._authStateChangedHandlers.push(authStateChangedHandler);
    const unsubscribe = this._createAuthStateChangedUnsubscribe(authStateChangedHandler);
    return unsubscribe;
  };

  /**
   * Refresh auth for a user
   * @returns {Promise<AuthEntity>} The updated representation of the current user
   * @memberof AuthAdapter
   */
  refreshAccessToken = async () => {
    return throwMethodNotImplementedError(`refreshAccessToken`);
  };
}

export default AuthAdapter;
